import { useContext, useEffect, useState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Search as SearchIcon, X } from "lucide-react";
import paths from "~/paths";
import SearchQuery from "~/searchQuery";
import savedSearchStore, { SavedSearchInfo } from "~/stores/savedSearch";
import { observer } from "mobx-react";
import AdvancedFiltersDisplay from "./AdvancedFiltersDisplay";
import SuggestionsList from "../SuggestionsList";
import { ReqStatus } from "~/util";
import DocumentsContext from "~/pages/Documents/DocumentsContext";
import { ToastNotificationContext } from "~/components/ToastNotificationContext";

interface Props {
  initialQuery: SearchQuery | null;
  searchQuery: SearchQuery;
  className?: string;
  onSearch: (searchQuery: SearchQuery) => void;
  onClear?: () => void;
  saveSearchEnabled?: boolean;
}

const SearchBar = observer((props: Props) => {
  const { searchQuery } = useContext(DocumentsContext);

  // suggestion list trigger
  const [focused, setFocused] = useState(false);
  // Local input state so typing doesn't re-render the whole Documents tree (and its
  // preview/skeleton) on every keystroke; the committed query is updated on submit.
  const [inputValue, setInputValue] = useState(searchQuery.searchAnywhere);
  useEffect(() => {
    setInputValue(searchQuery.searchAnywhere);
  }, [searchQuery.searchAnywhere]);
  const { showNotificationWithTimeout, setNotificationData } = useContext(ToastNotificationContext);

  useEffect(() => {
    (async () => {
      try {
        await savedSearchStore.f.load("", null);
      } catch (err) {}
    })();
  }, []);

  const [prevSearchQuery, setPrevSearchQuery] = useState<SearchQuery | null>(null);
  useEffect(() => {
    setPrevSearchQuery(props.initialQuery);
  }, [props.initialQuery]);

  const onSearch = (sq: SearchQuery) => {
    if (sq.isEmpty() && props.onClear) {
      props.onClear();
    }
    if (!sq.isValid()) return;
    setFocused(false);
    setPrevSearchQuery(sq);
    props.onSearch(sq);
  };

  const savedSearchEntry = savedSearchStore.isSaved(searchQuery);
  const emptyQuery = searchQuery.isEmpty();

  const [showSaveSearchModal, setShowSaveSearchModal] = useState<boolean>(false);
  const [savedSearchName, setSavedSearchName] = useState<string>("");
  const [chosenSearchQuery, setChosenSearchQuery] = useState<SavedSearchInfo | null>(null);
  const suggestions =
    chosenSearchQuery !== null
      ? savedSearchStore.f.itemsList.filter((a) => a.id !== chosenSearchQuery.id)
      : savedSearchStore.f.itemsList;

  useEffect(() => {
    setSavedSearchName(searchQuery.title);
  }, [searchQuery.title]);

  const canSaveSearch = savedSearchName.trim().length !== 0 || savedSearchStore.createReqStatus === ReqStatus.InProcess;

  const saveSearchEnabled = props.saveSearchEnabled ?? false;

  const saveSearch = async () => {
    savedSearchStore
      .saveQueryAs(searchQuery, savedSearchName)
      .then(() =>
        setNotificationData({ kind: "success", title: "Success", subtitle: "Search has been saved successfully" })
      )
      .catch(() =>
        setNotificationData({ kind: "error", title: "Search was not saved due to some API error", subtitle: "" })
      )
      .finally(() => {
        showNotificationWithTimeout();
        setShowSaveSearchModal(false);
      });
  };

  const saveSearchButton = (() => {
    if (!saveSearchEnabled || emptyQuery || savedSearchStore.f.loading) return null;
    if (!savedSearchEntry) {
      return (
        <a
          href="#"
          className="cursor-pointer text-sm text-primary-600 hover:underline"
          onClick={(e) => {
            e.preventDefault();
            setShowSaveSearchModal(true);
          }}
        >
          Save search
        </a>
      );
    }
    return (
      <a
        href={paths.construct.savedSearchManagement()}
        className="text-sm [color:var(--text-02)] hover:underline"
        title="Manage your saved searches"
      >
        Saved as "{savedSearchEntry!.description}"
      </a>
    );
  })();

  const shown = focused && suggestions.length !== 0 && !savedSearchStore.f.loading;

  return (
    <div className={props.className}>
      <Dialog
        open={showSaveSearchModal}
        onOpenChange={(open) => {
          if (!open) setShowSaveSearchModal(false);
        }}
      >
        <DialogContent className="sm:max-w-[480px]" aria-describedby={undefined}>
          <DialogHeader>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Search</p>
            <DialogTitle>Save this search</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Search description"
            value={savedSearchName}
            onChange={(e) => setSavedSearchName(e.target.value)}
          />
          <DialogFooter>
            <Button variant="secondary" onClick={() => setShowSaveSearchModal(false)}>
              Cancel
            </Button>
            <Button variant="default" disabled={!canSaveSearch} onClick={saveSearch}>
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="some-search"
            name="search-query"
            autoComplete="off"
            placeholder="Search..."
            value={inputValue}
            className="h-[40px] pl-9 pr-9"
            onFocus={() => setFocused(true)}
            onChange={(e) => setInputValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSearch(searchQuery.set("searchAnywhere", inputValue));
            }}
          />
          {!!inputValue && (
            <button
              type="button"
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setInputValue("")}
            >
              <X className="size-4" />
            </button>
          )}
          <div className="relative" style={{ zIndex: 99999999 }}>
            {shown && (
              <div
                className="animate-fade-in"
                style={{ boxShadow: "0px 0px 2px 0px #00000017, 0px 0px 8px 0px #00000012" }}
              >
                <SuggestionsList
                  onClose={() => setFocused(false)}
                  shown={shown}
                  suggestions={suggestions}
                  onSearch={(sq, saved) => {
                    setChosenSearchQuery(saved);
                    onSearch(sq);
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <Button
          onClick={() => onSearch(searchQuery.set("searchAnywhere", inputValue))}
          className="h-[40px] w-[120px] shrink-0 font-bold"
        >
          Search
        </Button>
      </div>
      <div className="mt-2 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <AdvancedFiltersDisplay onSearch={onSearch} searchQuery={prevSearchQuery} />
        </div>
        <div className="shrink-0 text-right">{saveSearchButton}</div>
      </div>
    </div>
  );
});

export default SearchBar;
